import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import Modal from '../../components/Modal';
import SearchableSelect from '../../components/SearchableSelect';
import SpellcheckPanel from '../../components/SpellcheckPanel';
import { listLanguages } from '../../api/languages';
import {
  bulkUploadTunePdfs,
  createLyrics,
  deleteLyrics,
  deleteLyricsTunePdf,
  listLyrics,
  updateLyrics,
  uploadLyricsTunePdf,
} from '../../api/lyrics';
import { listSongs } from '../../api/songs';
import { API_BASE_URL } from '../../config';

export default function LyricsSection({ token }) {
  const [lyricsList, setLyricsList] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ lyrics: '', language_id: '', song_id: '', yt_link: '' });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [tunePdfFile, setTunePdfFile] = useState(null);
  const [isSavingTunePdf, setIsSavingTunePdf] = useState(false);
  const [tunePdfError, setTunePdfError] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [isBulkUploading, setIsBulkUploading] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [lyricsData, languagesData, songsData] = await Promise.all([
        listLyrics(token),
        listLanguages(token),
        listSongs(token),
      ]);
      setLyricsList(lyricsData);
      setLanguages(languagesData);
      setSongs(songsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const languageName = (id) => languages.find((l) => l.language_id === id)?.name || `#${id}`;
  const songTitle = (songId) =>
    songId == null ? 'N/A' : songs.find((s) => s.song_id === songId)?.title || 'N/A';
  const selectedSong = songs.find((s) => s.song_id === Number(form.song_id));

  const openCreate = () => {
    setEditing(null);
    setForm({
      lyrics: '',
      language_id: languages[0]?.language_id ?? '',
      song_id: songs[0]?.song_id ?? '',
      yt_link: '',
    });
    setFormError('');
    setFormSuccess('');
    setTunePdfFile(null);
    setTunePdfError('');
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      lyrics: item.lyrics,
      language_id: item.language_id,
      song_id: item.song_id ?? '',
      yt_link: item.yt_link || '',
    });
    setFormError('');
    setFormSuccess('');
    setTunePdfFile(null);
    setTunePdfError('');
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleUploadTunePdf = async () => {
    if (!editing || !tunePdfFile) return;
    setIsSavingTunePdf(true);
    setTunePdfError('');
    try {
      const formData = new FormData();
      formData.append('pdf', tunePdfFile);
      const updated = await uploadLyricsTunePdf(token, editing.lyrics_id, formData);
      setEditing(updated);
      setTunePdfFile(null);
      await load();
    } catch (err) {
      setTunePdfError(err.message);
    } finally {
      setIsSavingTunePdf(false);
    }
  };

  const handleDeleteTunePdf = async () => {
    if (!editing) return;
    if (!window.confirm('Remove the song tune PDF?')) return;
    setIsSavingTunePdf(true);
    setTunePdfError('');
    try {
      const updated = await deleteLyricsTunePdf(token, editing.lyrics_id);
      setEditing(updated);
      await load();
    } catch (err) {
      setTunePdfError(err.message);
    } finally {
      setIsSavingTunePdf(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.song_id) {
      setFormError('Select which song this lyrics belongs to.');
      return;
    }
    setIsSaving(true);
    setFormError('');
    setFormSuccess('');
    try {
      if (editing) {
        const payload = {
          lyrics: form.lyrics,
          language_id: Number(form.language_id),
          song_id: Number(form.song_id),
          yt_link: form.yt_link,
        };
        await updateLyrics(token, editing.lyrics_id, payload);
        closeModal();
      } else {
        const formData = new FormData();
        formData.append('lyrics', form.lyrics);
        formData.append('language_id', form.language_id);
        formData.append('song_id', form.song_id);
        formData.append('yt_link', form.yt_link);
        if (tunePdfFile) formData.append('tune_pdf', tunePdfFile);

        const created = await createLyrics(token, formData);
        setEditing(created);
        setTunePdfFile(null);
        setFormSuccess('Lyrics saved.');
      }
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this lyrics entry?')) return;
    try {
      await deleteLyrics(token, item.lyrics_id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const openBulk = () => {
    setBulkFiles([]);
    setBulkResults(null);
    setBulkError('');
    setBulkOpen(true);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setIsBulkUploading(true);
    setBulkError('');
    setBulkResults(null);
    try {
      const formData = new FormData();
      bulkFiles.forEach((file) => formData.append('files', file));
      setBulkResults(await bulkUploadTunePdfs(token, formData));
      await load();
    } catch (err) {
      setBulkError(err.message);
    } finally {
      setIsBulkUploading(false);
    }
  };

  const canCreate = languages.length > 0 && songs.length > 0;

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Lyrics</h2>
        <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={openBulk}
          className="rounded-md border border-indigo-600 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
        >
          Bulk upload notation PDFs
        </button>
        <button
          onClick={openCreate}
          disabled={!canCreate}
          title={canCreate ? '' : 'Add a language and a song first'}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          + New lyrics
        </button>
        </div>
      </div>

      <ErrorBanner message={error} onRetry={load} />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Song</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Lyrics</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Language</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">YouTube</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Song Tune</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : lyricsList.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  No lyrics yet
                </td>
              </tr>
            ) : (
              lyricsList.map((item) => (
                <tr key={item.lyrics_id}>
                  <td className="px-4 py-2 text-gray-600">{songTitle(item.song_id)}</td>
                  <td className="px-4 py-2 text-gray-900 max-w-xs truncate">{item.lyrics}</td>
                  <td className="px-4 py-2 text-gray-600">{languageName(item.language_id)}</td>
                  <td className="px-4 py-2">
                    {item.yt_link ? (
                      <a
                        href={item.yt_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        Link
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {item.tune_pdf_filename ? (
                      <a
                        href={`${API_BASE_URL}/lyrics/${item.lyrics_id}/tune-pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                    <button
                      onClick={() => openEdit(item)}
                      className="text-gray-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {modalOpen && (
        <Modal title={editing ? 'Edit lyrics' : 'New lyrics'} onClose={closeModal} wide>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Song</label>
              <SearchableSelect
                options={songs}
                value={form.song_id}
                onChange={(songId) => setForm({ ...form, song_id: songId })}
                getOptionValue={(s) => s.song_id}
                getOptionLabel={(s) => s.title}
                getOptionSearchText={(s) =>
                  [s.title, s.title_ta, s.title_thanglish, s.song_number].filter(Boolean).join(' ')
                }
                placeholder="Search songs by title or number..."
              />
              {editing && !editing.song_id && (
                <p className="text-xs text-amber-600 mt-1">
                  This lyrics currently isn't attached to any song — pick one to attach it.
                </p>
              )}
              {selectedSong && (
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs text-gray-600 space-y-0.5">
                  <p>Song number: {selectedSong.song_number ?? '—'}</p>
                  <p>Scale: {selectedSong.scale}</p>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lyrics</label>
              <textarea
                required
                rows={8}
                spellCheck
                value={form.lyrics}
                onChange={(e) => setForm({ ...form, lyrics: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="mt-2">
                <SpellcheckPanel
                  token={token}
                  text={form.lyrics}
                  languageId={form.language_id}
                  excludeLyricsId={editing?.lyrics_id}
                  onTextChange={(lyrics) => setForm((current) => ({ ...current, lyrics }))}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <select
                required
                value={form.language_id}
                onChange={(e) => setForm({ ...form, language_id: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {languages.map((l) => (
                  <option key={l.language_id} value={l.language_id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                YouTube link (optional)
              </label>
              <input
                type="url"
                pattern="https?://(www\.|m\.)?(youtube\.com|youtu\.be)/.+"
                title="Paste a YouTube video link, or leave this empty"
                value={form.yt_link}
                onChange={(e) => setForm({ ...form, yt_link: e.target.value })}
                placeholder="https://youtube.com/..."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Shown as a video player above the lyrics in the app.
              </p>
            </div>
            {!editing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Song Tune PDF (optional)
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setTunePdfFile(e.target.files?.[0] || null)}
                  className="w-full text-sm"
                />
              </div>
            )}

            {formError && <p className="text-sm text-red-600">{formError}</p>}
            {formSuccess && <p className="text-sm text-indigo-600">{formSuccess}</p>}

            <button
              type="submit"
              disabled={isSaving}
              className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </form>

          {editing && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Song Tune PDF
              </label>
              {editing.tune_pdf_filename ? (
                <div className="flex items-center gap-3 text-sm mb-2">
                  <a
                    href={`${API_BASE_URL}/lyrics/${editing.lyrics_id}/tune-pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    View current PDF
                  </a>
                  <button
                    type="button"
                    onClick={handleDeleteTunePdf}
                    disabled={isSavingTunePdf}
                    className="text-red-600 hover:underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <p className="text-xs text-gray-500 mb-2">No song tune PDF uploaded yet.</p>
              )}
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setTunePdfFile(e.target.files?.[0] || null)}
                  className="flex-1 text-sm"
                />
                <button
                  type="button"
                  onClick={handleUploadTunePdf}
                  disabled={isSavingTunePdf || !tunePdfFile}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSavingTunePdf ? 'Uploading...' : editing.tune_pdf_filename ? 'Replace' : 'Upload'}
                </button>
              </div>
              {tunePdfError && <p className="text-sm text-red-600 mt-2">{tunePdfError}</p>}
            </div>
          )}
        </Modal>
      )}

      {bulkOpen && (
        <Modal title="Bulk upload music notation PDFs" onClose={() => setBulkOpen(false)} wide>
          <div className="space-y-3 text-sm">
            <p className="text-gray-600">
              Name each file with the song number first, e.g. <code>451.pdf</code> or{' '}
              <code>451 - May God be praised.pdf</code>. Each PDF is attached to every language of
              that song and replaces any notation it already has.
            </p>
            <input
              type="file"
              accept=".pdf"
              multiple
              onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
              className="w-full text-sm"
            />
            {bulkFiles.length > 0 && (
              <p className="text-gray-500">{bulkFiles.length} file(s) selected</p>
            )}
            <button
              type="button"
              onClick={handleBulkUpload}
              disabled={isBulkUploading || bulkFiles.length === 0}
              className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isBulkUploading ? 'Uploading...' : 'Upload'}
            </button>
            {bulkError && <p className="text-red-600">{bulkError}</p>}
            {bulkResults && (
              <div>
                <p className="font-medium text-gray-900 mb-2">
                  {bulkResults.filter((r) => r.status === 'ok').length} attached,{' '}
                  {bulkResults.filter((r) => r.status !== 'ok').length} failed
                </p>
                <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100 rounded border border-gray-200">
                  {bulkResults.map((r, i) => (
                    <li key={`${r.filename}-${i}`} className="px-3 py-2">
                      <span className={r.status === 'ok' ? 'text-green-700' : 'text-red-600'}>
                        {r.status === 'ok' ? '✓' : '✕'}
                      </span>{' '}
                      <span className="font-medium">{r.filename}</span>
                      {r.song_title && <span className="text-gray-500"> → {r.song_title}</span>}
                      <span className="block text-xs text-gray-500">{r.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
